import type { ReactNode } from 'react';

type Tone = 'neutral' | 'attention';

type Props = {
  /** Tile heading (e.g. "Requirements"). Rendered uppercase. */
  label: string;
  /** The prominent number (e.g. "12" or "—"). */
  big: string;
  /** Optional smaller "of N" suffix shown next to {@link big}. */
  small?: string;
  /** Optional one-line subtext below the number. */
  subtext?: string;
  /** Subtext colour — "attention" turns it amber (e.g. for deselected counts). */
  subtextTone?: Tone;
  /** Optional decorative icon (emoji or any node) in the top-right. */
  icon?: ReactNode;
};

/**
 * One stat tile in the v2 tender overview row.
 *
 * <p>Layout mirrors the wireframe (docs/wireframes/mvp-wireframes.html):
 * label up top, big number, optional "of N" suffix, optional subtext.</p>
 */
export default function StatTile({
  label,
  big,
  small,
  subtext,
  subtextTone = 'neutral',
  icon,
}: Props) {
  const subtextClass = subtextTone === 'attention' ? 'text-amber-700' : 'text-gray-500';
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-start justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
          {label}
        </p>
        {icon && (
          <span className="text-gray-300" aria-hidden="true">{icon}</span>
        )}
      </div>
      <p className="mt-3">
        <span className="text-3xl font-bold text-gray-900">{big}</span>
        {small && (
          <span className="text-base text-gray-400 font-normal ml-1">{small}</span>
        )}
      </p>
      {subtext && (
        <p className={`text-xs mt-1 ${subtextClass}`}>{subtext}</p>
      )}
    </div>
  );
}
